import { useEffect, useState } from "react";

import type {
  CloudSnapshotSummary,
  SnapshotComparison,
} from "@board-preserve/contracts";

import { compareCloudSnapshots } from "./cloud-connection";
import {
  buildComparisonHtml,
  buildComparisonJson,
  comparisonDownloadFilename,
  type ComparisonDownloadFormat,
} from "./comparison-download";
import { downloadBlob } from "./local-download";
import {
  comparisonChangeTotals,
  comparisonSelectionIsAvailable,
  readableComparisonToken,
} from "./snapshot-comparison-presentation";
import type { TrelloAuthorizationClient } from "./trello-authorization";

type CompareState = "failed" | "idle" | "loading" | "ready";

interface SnapshotComparisonPanelProps {
  apiUrl: string | null;
  boardName: string;
  client: TrelloAuthorizationClient;
  snapshots: CloudSnapshotSummary[];
}

export function SnapshotComparisonPanel({
  apiUrl,
  boardName,
  client,
  snapshots,
}: SnapshotComparisonPanelProps) {
  const [fromSnapshotId, setFromSnapshotId] = useState(snapshots[1]?.id ?? "");
  const [toSnapshotId, setToSnapshotId] = useState(snapshots[0]?.id ?? "");
  const [state, setState] = useState<CompareState>("idle");
  const [comparison, setComparison] = useState<SnapshotComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  useEffect(() => {
    const available = new Set(snapshots.map(({ id }) => id));
    if (
      comparisonSelectionIsAvailable(available, fromSnapshotId, toSnapshotId)
    ) {
      return;
    }
    const nextTo = available.has(toSnapshotId)
      ? toSnapshotId
      : (snapshots[0]?.id ?? "");
    const nextFrom =
      available.has(fromSnapshotId) && fromSnapshotId !== nextTo
        ? fromSnapshotId
        : (snapshots.find(({ id }) => id !== nextTo)?.id ?? "");
    setToSnapshotId(nextTo);
    setFromSnapshotId(nextFrom);
    setComparison(null);
    setState("idle");
    setDownloadMessage(null);
  }, [snapshots, fromSnapshotId, toSnapshotId]);

  function changeSelection(field: "from" | "to", snapshotId: string): void {
    if (field === "from") {
      setFromSnapshotId(snapshotId);
    } else {
      setToSnapshotId(snapshotId);
    }
    setComparison(null);
    setState("idle");
    setError(null);
    setDownloadMessage(null);
  }

  async function compareSelected(): Promise<void> {
    if (!fromSnapshotId || !toSnapshotId || fromSnapshotId === toSnapshotId) {
      return;
    }
    setState("loading");
    setError(null);
    setDownloadMessage(null);
    try {
      const session = await client.createCloudContextSession("cloud-compare");
      const result = await compareCloudSnapshots(
        apiUrl,
        session,
        fromSnapshotId,
        toSnapshotId,
      );
      setComparison(result);
      setState("ready");
    } catch (comparisonError) {
      setError(
        comparisonError instanceof Error
          ? comparisonError.message
          : "The selected retained backups could not be compared.",
      );
      setState("failed");
    }
  }

  function downloadComparison(format: ComparisonDownloadFormat): void {
    if (!comparison) {
      return;
    }
    const content =
      format === "json"
        ? buildComparisonJson(comparison)
        : buildComparisonHtml(comparison, boardName);
    const filename = comparisonDownloadFilename(comparison, format);
    downloadBlob(
      new Blob([content], {
        type:
          format === "json"
            ? "application/json;charset=utf-8"
            : "text/html;charset=utf-8",
      }),
      filename,
    );
    setDownloadMessage(`${filename} was sent to your downloads.`);
  }

  const changeTotals = comparison ? comparisonChangeTotals(comparison) : null;

  return (
    <section
      aria-labelledby="comparison-title"
      className="snapshot-comparison-workflow"
    >
      <div className="snapshot-comparison-setup">
        <header className="snapshot-comparison-heading">
          <span className="comparison-step-chip">Compare two backups</span>
          <strong id="comparison-title">Choose the versions to compare</strong>
          <p>
            Start with the earlier backup, then choose the later backup. Board
            Preserve will show what was added, changed, moved, or removed.
          </p>
        </header>
        <div className="snapshot-comparison-picker">
          <label htmlFor="comparison-from-snapshot">
            <span>Earlier backup</span>
            <select
              id="comparison-from-snapshot"
              onChange={(event) => changeSelection("from", event.target.value)}
              value={fromSnapshotId}
            >
              {snapshots.map((snapshot) => (
                <option
                  disabled={snapshot.id === toSnapshotId}
                  key={snapshot.id}
                  value={snapshot.id}
                >
                  {new Date(snapshot.capturedAt).toLocaleString()}
                </option>
              ))}
            </select>
          </label>
          <span className="comparison-direction" aria-hidden="true">
            →
          </span>
          <label htmlFor="comparison-to-snapshot">
            <span>Later backup</span>
            <select
              id="comparison-to-snapshot"
              onChange={(event) => changeSelection("to", event.target.value)}
              value={toSnapshotId}
            >
              {snapshots.map((snapshot) => (
                <option
                  disabled={snapshot.id === fromSnapshotId}
                  key={snapshot.id}
                  value={snapshot.id}
                >
                  {new Date(snapshot.capturedAt).toLocaleString()}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="snapshot-comparison-submit">
          <p>The comparison is read-only and makes no changes in Trello.</p>
          <button
            disabled={
              state === "loading" ||
              !fromSnapshotId ||
              !toSnapshotId ||
              fromSnapshotId === toSnapshotId
            }
            onClick={() => void compareSelected()}
            type="button"
          >
            {state === "loading" ? "Comparing backups…" : "Compare backups"}
          </button>
        </div>
      </div>
      {state === "loading" ? (
        <div className="snapshot-comparison-loading" role="status">
          <span aria-hidden="true" />
          <div>
            <strong>Comparing these recovery points</strong>
            <p>Checking the saved board structure and attachment records…</p>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="snapshot-comparison-error" role="alert">
          <strong>The backups could not be compared</strong>
          <p>{error}</p>
          <button onClick={() => void compareSelected()} type="button">
            Try again
          </button>
        </div>
      ) : null}
      {comparison && changeTotals ? (
        <div className="snapshot-comparison-result" aria-live="polite">
          <header className="snapshot-comparison-result-heading">
            <div>
              <span className="comparison-step-chip">Comparison complete</span>
              <strong>
                {comparison.totalChanges === 0
                  ? "No structural changes found"
                  : `${comparison.totalChanges} structural change${comparison.totalChanges === 1 ? "" : "s"} found`}
              </strong>
              <p>
                {new Date(comparison.from.capturedAt).toLocaleString()} →{" "}
                {new Date(comparison.to.capturedAt).toLocaleString()}
              </p>
            </div>
            <div className="snapshot-comparison-downloads">
              <button onClick={() => downloadComparison("html")} type="button">
                Download report
              </button>
              <button onClick={() => downloadComparison("json")} type="button">
                Download JSON
              </button>
            </div>
          </header>
          <dl className="snapshot-comparison-totals">
            {(["added", "changed", "moved", "removed"] as const).map((kind) => (
              <div className={`is-${kind}`} key={kind}>
                <dt>{readableComparisonToken(kind)}</dt>
                <dd>{changeTotals[kind]}</dd>
              </div>
            ))}
          </dl>
          {comparison.changes.length > 0 ? (
            <div className="snapshot-comparison-details">
              <div className="snapshot-comparison-details-heading">
                <strong>Changed items</strong>
                <span>
                  Showing {Math.min(comparison.changes.length, 8)} of{" "}
                  {comparison.changes.length}
                </span>
              </div>
              <ul>
                {comparison.changes.slice(0, 8).map((change) => (
                  <li
                    key={`${change.entityType}:${change.entityId}:${change.change}`}
                  >
                    <span className={`comparison-change is-${change.change}`}>
                      {readableComparisonToken(change.change)}
                    </span>
                    <div>
                      <strong>{change.label}</strong>
                      <span>
                        {readableComparisonToken(change.entityType)}
                        {change.changedFields.length > 0
                          ? ` · ${change.changedFields.map(readableComparisonToken).join(", ")}`
                          : ""}
                        {change.attachmentOutcome
                          ? ` · ${change.attachmentOutcome.from?.status ?? "not present"} → ${change.attachmentOutcome.to?.status ?? "not present"}`
                          : ""}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              {comparison.changes.length > 8 ? (
                <p className="snapshot-comparison-overflow">
                  The downloaded report includes the remaining{" "}
                  {comparison.changes.length - 8} item-level changes.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="snapshot-comparison-empty">
              <strong>These backups match</strong>
              <p>
                No supported board, list, card, or attachment changes were
                detected.
              </p>
            </div>
          )}
          <p className="snapshot-comparison-download-status" role="status">
            {downloadMessage ??
              "Reports are built in this browser and contain comparison details, not the full backup files."}
          </p>
        </div>
      ) : null}
    </section>
  );
}
